import React, { useState, useEffect } from 'react';
import {
  Stack,
  Group,
  Text,
  Badge,
  Paper,
  Title,
  Divider,
  Grid,
  Card,
  Alert,
  Loader,
  Center,
  Timeline
} from '@mantine/core';
import {
  IconBook,
  IconStar,
  IconSchool,
  IconCalendar,
  IconVideo,
  IconLivePhoto,
  IconClock
} from '@tabler/icons-react';
import { curriculumService } from '../../services/curriculumService';

const CurriculumDetails = ({ curriculum }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDetails();
  }, [curriculum.id]);

  const loadDetails = async () => {
    try {
      const data = await curriculumService.getCurriculum(curriculum.id);
      setDetails(data);
    } catch (error) {
      console.error('Failed to load curriculum details:', error);
    } finally {
      setLoading(false);
    }
  };

  const InfoCard = ({ icon: Icon, title, value, color = 'blue' }) => (
    <Card withBorder p="md">
      <Group>
        <Icon size={24} color={`var(--mantine-color-${color}-6)`} />
        <Stack gap="xs">
          <Text size="sm" c="dimmed" fw={500}>
            {title}
          </Text>
          <Text size="sm" fw={600}>
            {value}
          </Text>
        </Stack>
      </Group>
    </Card>
  );

  if (loading) {
    return (
      <Center h={300}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (!details) {
    return (
      <Alert color="red">
        Failed to load curriculum details
      </Alert>
    );
  }

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={3}>{details.title}</Title>
        <Group>
          {details.is_default && (
            <Badge color="gold" size="lg" leftSection={<IconStar size={16} />}>
              Default
            </Badge>
          )}
          <Badge color="blue" size="lg">
            {details.curriculum_classes?.length || 0} Classes
          </Badge>
        </Group>
      </Group>

      <Grid>
        <Grid.Col span={4}>
          <InfoCard
            icon={IconBook}
            title="Total Classes"
            value={details.curriculum_classes?.length || 0}
            color="blue"
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <InfoCard
            icon={IconSchool}
            title="Used in Batches"
            value={`${details.batches_count || 0} batches`}
            color="purple"
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <InfoCard
            icon={IconCalendar}
            title="Created"
            value={new Date(details.created_at).toLocaleDateString('en-IN')}
            color="green"
          />
        </Grid.Col>
      </Grid>

      <Divider label="Class Schedule" labelPosition="center" />

      {details.curriculum_classes && details.curriculum_classes.length > 0 ? (
        <Paper withBorder p="md">
          <Timeline active={details.curriculum_classes.length} bulletSize={24}>
            {details.curriculum_classes.map((curriculumClass, index) => {
              const classItem = curriculumClass.class;
              return (
                <Timeline.Item
                  key={curriculumClass.id}
                  bullet={
                    classItem.type === 'video_class' ? (
                      <IconVideo size={16} />
                    ) : (
                      <IconLivePhoto size={16} />
                    )
                  }
                  title={classItem.title}
                >
                  <Stack gap="xs">
                    <Group>
                      <Badge 
                        color={classItem.type === 'video_class' ? 'blue' : 'green'}
                        size="sm"
                      >
                        {classItem.type === 'video_class' ? 'Video Class' : 'Live Class'}
                      </Badge>
                      <Group gap={4}>
                        <IconClock size={14} />
                        <Text size="xs" c="dimmed">
                          {curriculumClass.schedule_time} (+{curriculumClass.plus_days} days)
                        </Text>
                      </Group>
                    </Group>
                    <Text size="sm" c="dimmed">
                      {classItem.short_description}
                    </Text>
                    <Text size="xs" c="dimmed">
                      Available: Day {curriculumClass.plus_days + 1} at {curriculumClass.schedule_time}
                    </Text>
                  </Stack>
                </Timeline.Item>
              );
            })}
          </Timeline>
        </Paper>
      ) : (
        <Paper withBorder p="xl">
          <Center>
            <Stack align="center">
              <IconBook size={48} color="gray" />
              <Text c="dimmed">No classes assigned to this curriculum</Text>
              <Text size="sm" c="dimmed">
                Use the Curriculum Builder to add classes
              </Text>
            </Stack>
          </Center>
        </Paper>
      )}

      <Divider label="Schedule Preview" labelPosition="center" />

      <Paper withBorder p="md">
        <Stack gap="xs">
          <Text fw={600} size="sm">How the schedule works:</Text>
          <Text size="xs" c="dimmed">
            • Classes become available based on the batch start time
          </Text>
          <Text size="xs" c="dimmed">
            • "plus_days" determines how many days after batch start
          </Text>
          <Text size="xs" c="dimmed">
            • "schedule_time" sets the exact time of availability
          </Text>
          <Text size="xs" c="dimmed">
            • Example: "+1 days at 06:00" means available at 6 AM the day after batch starts
          </Text>
        </Stack>
      </Paper>

      <Paper withBorder p="md">
        <Stack gap="xs">
          <Text fw={600} size="sm">System Information</Text>
          <Text size="xs" c="dimmed">
            ID: {details.id}
          </Text>
          <Text size="xs" c="dimmed">
            Created: {new Date(details.created_at).toLocaleString('en-IN')}
          </Text>
          <Text size="xs" c="dimmed">
            Last Updated: {new Date(details.updated_at).toLocaleString('en-IN')}
          </Text>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default CurriculumDetails;